import { ENTITY_ROLES } from './constants'

const norm = (s) => (typeof s === 'string' ? s.trim().toLowerCase() : '')

/**
 * Returns true when the user is an Xverse Instructor Candidate who should get read-only
 * access to a route that requires the Instructor entity role. Candidates must be a member
 * of the active entity — the flag alone doesn't grant cross-entity access.
 */
const candidateSatisfiesInstructorRoute = ({
  isXverseInstructorCandidate,
  requiredRoles,
  entityRoles,
  activeEntityId,
}) => {
  if (!isXverseInstructorCandidate) return false
  if (!activeEntityId) return false
  if (!requiredRoles.some((r) => norm(r) === norm(ENTITY_ROLES.INSTRUCTOR))) return false
  return entityRoles.some(
    (entityRole) =>
      entityRole?.entity?.id === activeEntityId && (entityRole.roles || []).length > 0,
  )
}

/**
 * RBAC for routes scoped to the active entity.
 *
 * @param {Object} props
 * @param {boolean} [props.isXverseAdmin] - bypass role checks (NOT the access gate)
 * @param {boolean} [props.isXverseInstructorCandidate] - grants read-only access to
 *   routes that require 'Instructor', provided the user is a member of the active entity
 * @param {string[]} [props.requiredRoles] - from route.handle.roles, e.g. ['Admin','Owner']
 * @param {string} [props.requiredAccess] - from route.handle.access, e.g. 'accessCustomCourses'.
 *   An account-level subscription/feature switch. It describes what the *account* may do, so it
 *   applies to everyone operating in the account — including Xverse Admins.
 * @param {Object} [props.entityAccess] - the active entity's live data (from useEntityAccess);
 *   the source of truth for access_* flags. Read here rather than the user's login profile so
 *   it covers Xverse Admins (no member role) and updates without a re-login.
 * @param {boolean} [props.entityAccessResolved] - whether those flags have loaded at all.
 *   Pass false while unknown so the access gate is skipped instead of denying.
 * @param {Array} [props.entityRoles] - user.entityRoles (array of { roles, entity: {id,...}, ... })
 * @param {string} [props.activeEntityId] - current entity id
 * @returns {boolean}
 */
export function userHasAccess({
  isXverseAdmin = false,
  isXverseInstructorCandidate = false,
  requiredRoles = [],
  requiredAccess = null,
  entityAccess = null,
  entityAccessResolved = true,
  entityRoles = [],
  activeEntityId,
}) {
  // Account capability gate (subscription/feature switches like accessCustomCourses).
  // Runs before the Xverse-admin bypass: it describes what the account may do, so a
  // feature the account lacks is hidden even from an admin viewing it. Reads the active
  // entity's live data. Skipped when there's no active entity (e.g. /admin section).
  //
  // "Not loaded yet" is not the same as "not allowed" (RSCX-385). Callers that
  // can't tell the difference pass entityAccessResolved: false, and the gate is
  // skipped rather than denying. Only the nav does this — PrivateRoute waits for
  // the flags before it renders, so nothing is actually reachable early.
  if (
    requiredAccess &&
    activeEntityId &&
    entityAccessResolved &&
    !entityAccess?.[requiredAccess]
  ) {
    return false
  }

  if (isXverseAdmin) return true

  // No roles on the route → treat as open to any authenticated user
  if (!requiredRoles || requiredRoles.length === 0) return true

  // If the route requires roles, we must have an active entity
  if (!activeEntityId) return false

  if (
    candidateSatisfiesInstructorRoute({
      isXverseInstructorCandidate,
      requiredRoles,
      entityRoles,
      activeEntityId,
    })
  ) {
    return true
  }

  // Admins and Owners have access to the Instructor routes
  const required = requiredRoles.map(norm).filter(Boolean)
  const permittedRoles = required.includes(norm(ENTITY_ROLES.INSTRUCTOR))
    ? [...required, norm(ENTITY_ROLES.ADMIN), norm(ENTITY_ROLES.OWNER)]
    : required

  const rolesForActive = entityRoles
    .filter((entityRole) => entityRole?.entity?.id === activeEntityId)
    .flatMap((entityRole) => (entityRole.roles || []).map(norm))
    .filter(Boolean)

  if (rolesForActive.length === 0) return false

  return permittedRoles.some((r) => rolesForActive.includes(r))
}

/**
 * Processes routes and generates navigation items for the sidebar
 * Handles grouping, role-based filtering, and path interpolation
 *
 * @param {Object} routes - Route object with children array
 * @param {Object} options - Navigation context
 * @param {string} options.activeEntity - Currently active entity ID
 * @param {string} [options.activeOrganization] - Currently active organization ID (RSCX-320)
 * @param {Array} options.roles - User's entity roles
 * @param {boolean} options.isXverseAdmin - User is an Xverse admin
 * @param {boolean} [options.isXverseInstructorCandidate] - User is an Xverse Instructor Candidate
 * @param {Object} [options.entityAccess] - active entity's live access flags (from useEntityAccess)
 * @param {boolean} [options.entityAccessResolved] - false while those flags have never
 *   loaded; the access gate is skipped rather than treating unknown as denied
 * @param {boolean} [options.hasPublishedFaqs] - whether any FAQ is published; routes flagged
 *   with handle.requiresPublishedFaqs are hidden until one is (RSCX-353)
 * @returns {Array} Array of navigation items ready for rendering
 */
export function getNavItems(
  routes,
  {
    activeEntity,
    activeOrganization = null,
    roles,
    isXverseAdmin,
    isXverseInstructorCandidate = false,
    entityAccess = null,
    entityAccessResolved = true,
    hasPublishedFaqs = false,
  },
) {
  /**
   * Safely combines parent and child paths
   * Handles index routes, absolute paths, and relative paths
   *
   * @param {string} parentPath - Parent path
   * @param {string} childPath - Child path
   */
  const buildPath = (parentPath, childPath) => {
    // Index route
    if (!childPath) return parentPath

    // Absolute path
    if (childPath.startsWith('/')) return childPath

    // Relative path
    return `${parentPath}/${childPath}`.replace('//', '/')
  }

  /**
   * Checks if the user has access to a route with the given required roles. Delegates to
   * {@link userHasAccess} so nav gating and route gating cannot drift apart.
   */
  const hasRequiredRole = (requiredRoles = [], requiredAccess = null) =>
    userHasAccess({
      isXverseAdmin,
      isXverseInstructorCandidate,
      requiredRoles,
      requiredAccess,
      entityAccess,
      entityAccessResolved,
      entityRoles: roles ?? [],
      activeEntityId: activeEntity,
    })

  /**
   * Recursively processes route children into navigation items
   */
  const processRoutes = (children, parentPath = '') =>
    children
      .filter((route) => {
        // Only include routes marked for navigation
        if (!route.handle?.showInNav) return false

        // Content-dependent nav: hide until RescueX has published an FAQ
        if (route.handle?.requiresPublishedFaqs && !hasPublishedFaqs) return false

        const entityScoped = route.handle?.entityScoped ?? false
        const requiredRoles = route.handle?.roles ?? []
        const requiredAccess = route.handle?.access ?? null

        // For non-entity-scoped routes with no active entity, allow access
        if (!entityScoped && !activeEntity) return true

        // For all other routes, check role + account-access requirements
        return hasRequiredRole(requiredRoles, requiredAccess)
      })
      .map((route) => {
        // Build the full navigation path
        let fullPath = route.index ? parentPath : buildPath(parentPath, route.path)

        // Replace entity ID placeholder with actual entity ID
        if (activeEntity) {
          fullPath = fullPath.replace(':entityId', activeEntity)
        }

        // Replace organization ID placeholder for org-scoped nav (RSCX-320)
        if (activeOrganization) {
          fullPath = fullPath.replace(':organizationId', activeOrganization)
        }

        // Handle grouped navigation items (collapsible sections)
        if (route.handle?.group && route.children) {
          return {
            title: route.handle.title || 'Untitled',
            icon: route.handle.icon || null,
            url: fullPath,
            isGroup: true,

            // Recursively process group children
            items: processRoutes(route.children, fullPath),
          }
        }

        // Handle individual navigation items
        return {
          title: route.handle?.title || 'Untitled',
          icon: route.handle?.icon || null,
          url: fullPath,
          isGroup: false,
        }
      })

  return processRoutes(routes.children || [])
}
